<template>
  <div data-name="ResponsibilityCard" class="responsibility-card-wrapper">
    <ResponsibilityCardHeader
      :breadcrumbs="computedBreadcrumbs"
      :status="responsibilityDetails?.status || ''"
      :code="getOrgCodeName(responsibilityDetails) || ''"
      :name="responsibilityDetails?.name || ''"
      :archived="responsibilityDetails?.archived || false"
      :avg_readiness="responsibilityDetails?.avg_readiness || 0"
      :dataStream="chatterList"
      :totalChatterCount="totalChatterCount"
      :currentResponsibility="responsibilityDetails"
      :chatterDrawerOpen="chatterDrawerOpen"
      @update:chatterDrawerOpen="chatterDrawerOpen = $event"
      @reloadChatterList="handleReloadChatterList"
      @requestMoreCheckpointsChatter="handleRequestMoreChatter"
      @optionClick="handleOptionClick"
      @backToList="handleBreadcrumbNavigation"
    />
    <ResponsibilityCardMenu
    class="responsibility-card-menu"
      :checklist_count="checklist_count"
      :badge_holders_count="badge_holders_count"
      :assignments_count="assignments_count"
      @tab-change="handleTabChange"
      :selectedTab="selectedTab"
      :items="menuItems"
    />
    <div class="responsibility-card-content">
      <ResponsibilityPurposeTab
        v-if="selectedTab === 0"
        :value="responsibilityDetails?.purpose || ''"
        :hideAIButtons="true"
        :isSaving="isSavingPurpose"
        @save="handlePurposeSave($event)"
        :maxLength="1000"
      />
      <EditCheckpointsTab
        v-if="selectedTab === 1"
        :responsibilityId="computedResponsibilityId"
        :responsibilityCheckpointCounts="responsibilityCheckpointCounts"
        :responsibilityNameProp="responsibilityDetails?.name || ''"
        :responsibilityCodeProp="getOrgCodeName(responsibilityDetails)"
        :responsibilityPurposeProp="responsibilityDetails?.purpose || ''"
        @checkpointCountChanged="handleCheckpointCountChanged"
        @drilldown="handleDrilldown"
      />
      <ResponsibilityBadgeHoldersTab
        v-if="selectedTab === 2"
        :responsibility-id="computedResponsibilityId"
        :responsibilityData="responsibilityDetails"
        @badgeCountsUpdated="handleBadgeCountsUpdated"
        :filter-levels="badgeHolderFilterLevels"
      />
      <ResponsibilityAssignmentsTab
        v-if="selectedTab === 3"
        :current-response="assignmentsCurrentResponse"
        :data-stream="assignmentsDataStream"
        :responsibility-id="computedResponsibilityId"
        :account-id="accountIdComputed"
        :avg_readiness="responsibilityDetails?.avg_readiness || 0"
        @weight-changed="handleWeightChanged"
        @remove-badge="handleRemoveBadge"
      />
      <ResponsibilityDefaultsTab
        v-if="selectedTab === 4"
        :responsibilityId="computedResponsibilityId"
        @createDefaultBadge="handleCreateDefaultBadge"
      />
    </div>

    <!-- Dialog Components -->
    <RenameResponsibility
      v-model="showRenameDialog"
      :responsibility="responsibilityName"
      @onSave="handleRenameResponsibility"
    />
    <DeleteResponsibility
      v-model="showDeleteDialog"
      :responsibility="responsibilityName"
      @onDelete="handleDeleteResponsibility"
    />
    <ActivateResponsibility
      v-model="showActivateDialog"
      :responsibility="responsibilityName"
      @onSave="handleActivateResponsibility"
    />
    <RetireResponsibility
      v-model="showRetireDialog"
      :responsibility="responsibilityName"
      @onSave="handleRetireResponsibility"
    />
    <ReActivateResponsibility
      v-model="showReActivateDialog"
      :responsibility="responsibilityName"
      @onSave="handleReActivateResponsibility"
    />
    <ArchivePopUp
      :action="archiveAction"
      type="responsibility"
      :show="showArchiveDialog"
      :curretObj="currentOrgResponsibility"
      @onClose="showArchiveDialog = false"
      @onArchive="handleArchiveResponsibility"
    />
    <AttachedResponsibilityToRole
      v-model="showAttachRoleDialog"
      OrgObjectType="Responsibility to Role"
      @onSave="handleAttachToRole"
      :type="
        showAttachResponsibilitiesPicker
          ? 'respToRole_new_picker'
          : 'respToRole'
      "
      :items="attachableRoles"
      @item-selected="handleRoleSelected"
      @add-item-selected="handleAddNewRole"
      :parent-name="responsibilityName"
      :hasMoreData="hasMoreData"
      :isLoadingMore="isLoadingMore"
      @load-more="loadMoreRoles"
      @search="searchRoles"
      :isAssigning="isAssigningRole"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, getCurrentInstance } from "vue";
import ResponsibilityCardMenu from "@components/ResponsibilityCardMenu/ResponsibilityCardMenu.vue";
import ResponsibilityPurposeTab from "@components/ResponsibilityPurposeTab/ResponsibilityPurposeTab.vue";
import EditCheckpointsTab from "@components/EditCheckpointsTab/EditCheckpointsTab.vue";
import ResponsibilityBadgeHoldersTab from "@components/ResponsibilityBadgeHoldersTab/ResponsibilityBadgeHoldersTab.vue";
import ResponsibilityAssignmentsTab from "@components/ResponsibilityAssignmentsTab/ResponsibilityAssignmentsTab.vue";
import ResponsibilityDefaultsTab from "@components/ResponsibilityDefaultsTab/ResponsibilityDefaultsTab.vue";
import ResponsibilityCardHeader from "@components/ResponsibilityCardHeader/ResponsibilityCardHeader.vue";
// Dialog Components
import RenameResponsibility from "@components/RolesCatalog/RenameResponsibility.vue";
import DeleteResponsibility from "@components/RolesCatalog/DeleteResponsibility.vue";
import ActivateResponsibility from "@components/RolesCatalog/ActivateResponsibility.vue";
import RetireResponsibility from "@components/RolesCatalog/RetireResponsibility.vue";
import ReActivateResponsibility from "@components/RolesCatalog/ReActivateResponsibility.vue";
import ArchivePopUp from "@components/common/ArchivePopUp.vue";
import AttachedResponsibilityToRole from "@components/RolesCatalog/AttachedResponsibilityToRole.vue";
// API imports
import {
  getResponsibilityIdV2,
  getResponsibilityChatter,
  getResponsibilityChatterV2,
  responsibilityStateUpdate,
  deleteResponsibility,
  updateActivateResponsibility,
  updateRetireResponsibility,
  updateReActivateResponsibility,
  updateArchiveResponsibility,
  updateUnArchiveResponsibility,
  attachResponsibilityToRole,
  fetchAttachableRoles,
  updateResponsibilityPurpose,
} from "@/api";
const props = defineProps({
  // Purpose Tab Props
  purposeValue: {
    type: String,
    default: "",
  },
  purposePlaceholder: {
    type: String,
    default: "Enter purpose for this responsibility...",
  },
  purposeMaxLength: {
    type: Number,
    default: 500,
  },
  purposeIsSaving: {
    type: Boolean,
    default: false,
  },
  // Badge Holders Tab Props
  badgeHoldersResponsibilityId: {
    type: [Number, String],
    default: 1,
  },
  // Assignments Tab Props
  assignmentsCurrentResponse: {
    type: Object,
    default: () => ({}),
  },
  assignmentsDataStream: {
    type: Array,
    default: () => [],
  },
  status: {
    type: String,
    default: "",
  },
  code: {
    type: String,
    default: "",
  },
  name: {
    type: String,
    default: "",
  },
  avgReadiness: {
    type: Number,
    default: 0,
  },
  // Optional resId prop - if provided, uses this instead of route params
  resId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits([
  "tab-change",
  "purpose-input",
  "purpose-save",
  "purpose-discard",
  "add-purpose",
  "purpose-edit",
  "purpose-improve",
  "purpose-suggest",
  "weight-changed",
  "remove-badge",
  "create-default-badge",
  "drilldown",
]);

const selectedTab = ref(0);
const responsibilityDetails = ref(null);
const checklist_count = ref(0);
const badge_holders_count = ref(0);
const assignments_count = ref(0);
const isSavingPurpose = ref(false);

// Chatter data
const chatterList = ref([]);
const totalChatterCount = ref(0);
const chatterDrawerOpen = ref(false);
const loadedChatterPages = ref(new Set());

// Dialog visibility refs
const showRenameDialog = ref(false);
const showDeleteDialog = ref(false);
const showActivateDialog = ref(false);
const showRetireDialog = ref(false);
const showReActivateDialog = ref(false);
const showArchiveDialog = ref(false);
const showAttachRoleDialog = ref(false);
const archiveAction = ref(null);
const attachableRoles = ref([]);
const selectedRoleId = ref(null);
const isAssigningRole = ref(false);

// Pagination refs for attach role dialog
const hasMoreData = ref(false);
const isLoadingMore = ref(false);
const currentPage = ref(1);
const currentSearchTerm = ref("");
const totalCount = ref(0);

// Computed property for archive dialog
const currentOrgResponsibility = computed(() => {
  if (!responsibilityDetails.value) return null;
  return {
    realId: responsibilityDetails.value.id,
    data: responsibilityDetails.value,
  };
});

const showAttachResponsibilitiesPicker = computed(() => {
  return proxy.$store.getters["featureFlags/ideaMDP389"];
});

// Computed property for responsibility name (to avoid optional chaining in template)
const responsibilityName = computed(() => {
  return responsibilityDetails.value?.name || "";
});

const instance = getCurrentInstance();
const proxy = instance?.proxy;

const computedBreadcrumbs = computed(() => {
  const route = proxy?.$route;
  
  // Check if we're coming from position → role → responsibility
  const positionId = route?.params?.positionId;
  const positionName = route?.query?.positionName;
  const roleId = route?.params?.roleId;
  const roleName = route?.query?.roleName;
  const roleCode = route?.query?.roleCode;
  
  // Case 1: Position → Role → Responsibility
  if (positionId && roleId && roleName) {
    const roleDisplayText = roleCode ? `${roleCode} ${roleName}` : roleName;
    
    return [
      {
        text: "Positions",
        href: "",
        disabled: false,
      },
      {
        text: positionName || "Position",
        href: "",
        disabled: false,
        icon: "org_icon_position",
        positionId: positionId,
      },
      {
        text: roleDisplayText,
        href: "",
        disabled: false,
        icon: "org_icon_role",
        roleId: roleId,
      },
      {
        text: "Responsibility",
        href: "",
        disabled: true,
      }
    ];
  }
  
  // Case 2: Role → Responsibility
  if (roleId && roleName) {
    const roleDisplayText = roleCode ? `${roleCode} ${roleName}` : roleName;
    
    return [
      {
        text: "Roles",
        href: "",
        disabled: false,
      },
      {
        text: roleDisplayText,
        href: "",
        disabled: false,
        icon: "org_icon_role",
        roleId: roleId,
      },
      {
        text: "Responsibility",
        href: "",
        disabled: true,
      }
    ];
  }
  
  // Case 3: Standalone Responsibility
  return [
    {
      text: "Responsibilities",
      href: "",
      disabled: false,
    },
    {
      text: "Responsibility",
      href: "",
      disabled: true,
    }
  ];
});

const tabNameToIndex = {
  purpose: 0,
  checklist: 1,
  "badge-holders": 2,
  assignments: 3,
  defaults: 4,
};

// Map tab index to query param name
const tabIndexToName = {
  0: "purpose",
  1: "checklist",
  2: "badge-holders",
  3: "assignments",
  4: "defaults",
};

// Set initial tab from route query (check both 'tab' and 'resTab')
onMounted(async () => {
  const tabParam = proxy?.$route?.query?.resTab || proxy?.$route?.query?.tab;
  if (tabParam && tabNameToIndex[tabParam] !== undefined) {
    selectedTab.value = tabNameToIndex[tabParam];
  }

  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;
  
  // Only fetch if we have a valid resId
  if (!resId) {
    console.warn("ResponsibilityCard mounted without resId");
    return;
  }
  
  try {
    await fetchResponsibilityDetails(accountId, resId);
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404) {
      proxy?.$store?.dispatch("routeErrorHandling/setError", {
        code: 404,
        message: "Responsibility not found.",
      });
    } else {
      throw error;
    }
  }
});

// Watch for route query changes (check both 'tab' and 'resTab')
watch(
  () => proxy?.$route?.query?.resTab || proxy?.$route?.query?.tab,
  (newTab) => {
    if (newTab && tabNameToIndex[newTab] !== undefined) {
      selectedTab.value = tabNameToIndex[newTab];
    }
  }
);

// Compute badge holder filter levels from route query
const badgeHolderFilterLevels = computed(() => {
  const filter = proxy?.$route?.query?.filter;
  if (filter === "coach") {
    return ["Coach"];
  } else if (filter === "master") {
    return ["Master"];
  }
  return [];
});

// Compute responsibility ID from prop or route
const computedResponsibilityId = computed(() => {
  return props.resId || proxy?.$route?.params?.resId || null;
});

// Compute account ID from localStorage
const accountIdComputed = computed(() => {
  return JSON.parse(localStorage.getItem("currentAccountID"));
});

const responsibilityCheckpointCounts = computed(() => {
  if (!responsibilityDetails.value) {
    return {
      Apprentice: 0,
      Professional: 0,
      Coach: 0,
      Master: 0,
    };
  }

  return {
    Apprentice: responsibilityDetails.value.count_apprentice_checkpoints || 0,
    Professional:
      responsibilityDetails.value.count_professional_checkpoints || 0,
    Coach: responsibilityDetails.value.count_coach_checkpoints || 0,
    Master: responsibilityDetails.value.count_master_checkpoints || 0,
  };
});

const menuItems = computed(() => [
  { title: "Purpose", count: null },
  { title: "Checklist", count: checklist_count.value },
  { title: "Badge Holders", count: badge_holders_count.value },
  { title: "Assignments", count: assignments_count.value },
  { title: "Expiration Defaults", count: null },
]);

async function fetchResponsibilityDetails(accountId, resId) {
  try {
    proxy.$loader.show();
    const response = await getResponsibilityIdV2(
      accountId,
      resId,
      "responsibility"
    );
    responsibilityDetails.value = response.data;
    checklist_count.value =
      response.data?.active_apprentice_checkpoints +
      response.data?.active_coach_checkpoints +
      response.data?.active_master_checkpoints +
      response.data?.active_professional_checkpoints;
    badge_holders_count.value = response.data?.badges_count;
    assignments_count.value = response.data?.assignments_count;
  } catch (error) {
    console.error("Error fetching responsibility details:", error);
    throw error;
  } finally {
    proxy.$loader.hide();
  }
}

function handleTabChange(value) {
  selectedTab.value = value;
  
  // Update route query with new tab (use resTab for responsibility detail routes)
  const tabName = tabIndexToName[value];
  if (tabName && proxy?.$router) {
    const query = { ...proxy.$route.query };
    // Always use resTab for responsibility cards (we're using params.resId now)
    query.resTab = tabName;
    // Remove old tab parameter if it exists
    delete query.tab;
    proxy.$router.replace({ query }).catch(() => {});
  }
  
  emit("tab-change", value);
}

function handleBreadcrumbNavigation(breadcrumb) {
  const route = proxy?.$route;
  const router = proxy?.$router;
  
  if (!router) return;
  
  const positionId = route?.params?.positionId;
  const roleId = route?.params?.roleId;
  
  // Navigate to Positions list
  if (breadcrumb?.text === "Positions") {
    router.push({
      name: 'NewOrganization',
      params: { tab: 'positions' }
    }).catch(() => {});
  }
  // Navigate to Roles list
  else if (breadcrumb?.text === "Roles") {
    router.push({
      name: 'NewOrganization',
      params: { tab: 'roles' }
    }).catch(() => {});
  }
  // Navigate to Responsibilities list
  else if (breadcrumb?.text === "Responsibilities") {
    router.push({
      name: 'NewOrganization',
      params: { tab: 'responsibilities' }
    }).catch(() => {});
  }
  // Navigate back to Position detail
  else if (breadcrumb?.positionId && !breadcrumb.disabled) {
    router.push({
      name: 'PositionDetailChild',
      params: { positionId: breadcrumb.positionId },
      query: { positionTab: 'attached-roles' }
    }).catch(() => {});
  }
  // Navigate back to Role detail
  else if (breadcrumb?.roleId && !breadcrumb.disabled) {
    // If we have both positionId and roleId, navigate to role from position
    if (positionId) {
      router.push({
        name: 'RoleDetailFromPosition',
        params: { 
          positionId: positionId,
          roleId: breadcrumb.roleId 
        },
        query: { 
          positionName: route?.query?.positionName,
          roleTab: 'responsibilities' 
        }
      }).catch(() => {});
    } else {
      // Otherwise, navigate to standalone role
      router.push({
        name: 'RoleDetailChild',
        params: { roleId: breadcrumb.roleId },
        query: { roleTab: 'responsibilities' }
      }).catch(() => {});
    }
  }
}

async function handleCheckpointCountChanged() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;
  if (accountId && resId) {
    await fetchResponsibilityDetails(accountId, resId);
  }
}

function handleDrilldown(data) {
  emit("drilldown", data);
}

function handlePurposeInput(value) {
  emit("purpose-input", value);
}

async function handlePurposeSave(value) {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  try {
    isSavingPurpose.value = true;
    await updateResponsibilityPurpose(accountId, resId, {
      purpose: value,
      account: accountId,
    });
    await fetchResponsibilityDetails(accountId, resId);
  } catch (error) {
    console.error("Error saving purpose:", error);
  } finally {
    isSavingPurpose.value = false;
  }
}

function handlePurposeDiscard() {
  emit("purpose-discard");
}

function handleAddPurpose() {
  emit("add-purpose");
}

function handlePurposeEdit() {
  emit("purpose-edit");
}

function handlePurposeImprove(value) {
  emit("purpose-improve", value);
}

function handlePurposeSuggest() {
  emit("purpose-suggest");
}

// Assignments Tab Handlers
function handleWeightChanged(data) {
  emit("weight-changed", data);
}

function handleRemoveBadge(data) {
  emit("remove-badge", data);
}

// Defaults Tab Handlers
function handleCreateDefaultBadge(data) {
  emit("create-default-badge", data);
}

async function handleBadgeCountsUpdated() {
  // Refresh responsibility details to get updated badge counts
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;
  await fetchResponsibilityDetails(accountId, resId);
}

function getOrgCodeName(data) {
  if (data?.code_str) {
    return data?.code_str.split(" ")[0];
  } else {
    return "#" + data?.code;
  }
}

// Chatter functions
async function fetchChatterList(isLoadMore = false) {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  if (!accountId || !resId) return;

  if (!isLoadMore) {
    chatterList.value = [];
    loadedChatterPages.value.clear();
  }

  const currentLength = chatterList.value.length;
  const page = Math.floor(currentLength / 10) + 1;

  // Don't reload already loaded pages
  if (loadedChatterPages.value.has(page)) {
    return;
  }

  try {
    const { data } = await getResponsibilityChatterV2(accountId, resId, page, "responsibility");
    loadedChatterPages.value.add(page);

    if (data && data.count) {
      totalChatterCount.value = data.count;
    }

    if (data && data.results) {
      const chatterItems = data.results.map((el) => {
        const date = new Date(el.recorded_on);
        const milliseconds = date.getTime();
        const millisecondsString = milliseconds.toString();
        const nameParts = el.recorded_by_name.split(" ");
        
        const newEl = {
          id: el.id,
          IsExIQtiveBot: false,
          message: el.note,
          created_on: millisecondsString,
          IsHistory: el.is_history,
        };
        
        if (nameParts.length === 2) {
          newEl.avatar = {
            picture: "",
            firstName: nameParts[0],
            lastName: nameParts[1],
          };
        } else {
          newEl.avatar = {
            picture: "",
            firstName: el.recorded_by_name,
            lastName: "",
          };
        }
        
        return newEl;
      });
      chatterList.value.push(...chatterItems);
    }
  } catch (error) {
    console.error("Error fetching chatter:", error);
  }
}

function handleReloadChatterList() {
  fetchChatterList(false);
}

function handleRequestMoreChatter() {
  fetchChatterList(true);
}

// Dropdown Menu Handler
function handleOptionClick(operation) {
  const action = operation.item?.value;

  switch (action) {
    case "activate_action":
      showActivateDialog.value = true;
      break;
    case "rename_action":
      showRenameDialog.value = true;
      break;
    case "delete_action":
      showDeleteDialog.value = true;
      break;
    case "retire_action":
      showRetireDialog.value = true;
      break;
    case "attach_role":
      // Reset pagination state
      currentPage.value = 1;
      currentSearchTerm.value = "";
      hasMoreData.value = true;
      loadAttachableRoles(1, "", false);
      showAttachRoleDialog.value = true;
      break;
    case "reactivate_action":
      showReActivateDialog.value = true;
      break;
    case "archive_action":
      archiveAction.value = "archive";
      showArchiveDialog.value = true;
      break;
    case "unarchive_action":
      archiveAction.value = "unarchive";
      showArchiveDialog.value = true;
      break;
    default:
  }
}

// Dialog Action Handlers
async function handleRenameResponsibility(newName) {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  try {
    proxy.$loader.setAppLoading(true);
    await responsibilityStateUpdate(accountId, resId, {
      name: newName,
      account: accountId,
    });
    showRenameDialog.value = false;
    await fetchResponsibilityDetails(accountId, resId);
  } catch (error) {
    console.error("Error renaming responsibility:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
}

async function handleDeleteResponsibility() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  try {
    proxy.$loader.setAppLoading(true);
    await deleteResponsibility(accountId, resId);
    showDeleteDialog.value = false;
    proxy.$router.push("/new-organization/responsibilities");
  } catch (error) {
    console.error("Error deleting responsibility:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
}

async function handleActivateResponsibility() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  try {
    proxy.$loader.setAppLoading(true);
    await updateActivateResponsibility(accountId, resId);
    showActivateDialog.value = false;
    await fetchResponsibilityDetails(accountId, resId);
  } catch (error) {
    console.error("Error activating responsibility:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
}

async function handleRetireResponsibility() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  try {
    proxy.$loader.setAppLoading(true);
    await updateRetireResponsibility(accountId, resId, {
      status: "retired",
      name: responsibilityDetails.value?.name,
      account: accountId,
    });
    showRetireDialog.value = false;
    await fetchResponsibilityDetails(accountId, resId);
  } catch (error) {
    console.error("Error retiring responsibility:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
}

async function handleReActivateResponsibility() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  try {
    proxy.$loader.setAppLoading(true);
    await updateReActivateResponsibility(accountId, resId, {
      status: "active",
      name: responsibilityDetails.value?.name,
      account: accountId,
    });
    showReActivateDialog.value = false;
    await fetchResponsibilityDetails(accountId, resId);
  } catch (error) {
    console.error("Error re-activating responsibility:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
}

async function handleArchiveResponsibility() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  try {
    proxy.$loader.setAppLoading(true);
    if (archiveAction.value === "archive") {
      await updateArchiveResponsibility(accountId, "responsibility", resId);
    } else {
      await updateUnArchiveResponsibility(accountId, "responsibility", resId);
    }
    showArchiveDialog.value = false;
    await fetchResponsibilityDetails(accountId, resId);
  } catch (error) {
    console.error("Error archiving/unarchiving responsibility:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
}

async function loadAttachableRoles(
  page = 1,
  searchTerm = "",
  isLoadMore = false
) {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;
  const limit = 10;
  const offset = (page - 1) * limit;

  try {
    if (!isLoadMore) {
      attachableRoles.value = [];
    }
    isLoadingMore.value = true;

    const response = await fetchAttachableRoles(
      accountId,
      resId,
      limit,
      offset,
      searchTerm
    );

    if (response?.data?.results) {
      const newItems = response.data.results.map((role) => ({
        id: role.id,
        code: role.code_str?.match(/\d+/)?.[0] || role.code,
        name: role.name,
        status: role.status,
        attached: role?.attached_to_responsibility,
      }));

      if (isLoadMore && attachableRoles.value) {
        attachableRoles.value = [...attachableRoles.value, ...newItems];
      } else {
        attachableRoles.value = newItems;
      }

      currentPage.value = page;
      totalCount.value = response.data.count || 0;
      hasMoreData.value = page * limit < totalCount.value;
    }
  } catch (error) {
    console.error("Error loading roles:", error);
  } finally {
    isLoadingMore.value = false;
  }
}

async function loadMoreRoles() {
  if (isLoadingMore.value || !hasMoreData.value) {
    return;
  }
  const nextPage = currentPage.value + 1;
  await loadAttachableRoles(nextPage, currentSearchTerm.value, true);
}

async function searchRoles(searchTerm) {
  currentSearchTerm.value = searchTerm;
  currentPage.value = 1;
  hasMoreData.value = true;
  await loadAttachableRoles(1, searchTerm, false);
}

function handleRoleSelected(role) {
  selectedRoleId.value = role?.id;
}

async function handleAddNewRole(item) {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  try {
    proxy.$loader.show();
    const payload = {
      name: item.name,
      account: accountId,
    };
    // Create new role via store dispatch
    const newRole = await proxy.$store.dispatch(
      "readinessCatalogsStore/createRole",
      { role: payload, accountId: accountId }
    );
    selectedRoleId.value = newRole.id;
    // Attach the responsibility to the newly created role
    await attachResponsibilityToRole(accountId, newRole.id, {
      attachment_id: resId,
    });
    showAttachRoleDialog.value = false;
    // Reset pagination state
    currentPage.value = 1;
    currentSearchTerm.value = "";
    selectedRoleId.value = null;
    await fetchResponsibilityDetails(accountId, resId);
  } catch (error) {
    console.error("Error creating and attaching to role:", error);
  } finally {
    proxy.$loader.hide();
  }
}

async function handleAttachToRole() {
  if (!selectedRoleId.value) return;

  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  try {
    isAssigningRole.value = true;
    proxy.$loader.setAppLoading(true);
    // Attach responsibility to the selected role
    await attachResponsibilityToRole(accountId, selectedRoleId.value, {
      attachment_id: resId,
    });
    showAttachRoleDialog.value = false;
    // Reset state
    selectedRoleId.value = null;
    currentPage.value = 1;
    currentSearchTerm.value = "";
    await fetchResponsibilityDetails(accountId, resId);
  } catch (error) {
    console.error("Error attaching responsibility to role:", error);
  } finally {
    isAssigningRole.value = false;
    proxy.$loader.setAppLoading(false);
  }
}
</script>

<style scoped>
.responsibility-card-content {
  width: 100%;
  margin-top: 24px;
}
.responsibility-card-wrapper {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 1px 2px 0px #1018280F, 0px 1px 3px 0px #1018281A;
  border: 1px solid #EAECF0;
  width: 100%;
  padding: 24px 12px 12px 12px;
}
.responsibility-card-menu {
  margin-top: 12px;
}
</style>
